import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import SlidingPane from "react-sliding-pane";
import { ToastContainer, toast } from 'react-toastify';
import Select from "react-select"

import MaskedInput from 'react-text-mask'
import { Button, DatePicker as SetlistDatePicker, TextInput } from "@opendate-app/setlist";
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import { promoCodeMask } from '../../shared/promoCodeValidation';
import { mergeSelectStyles } from '../../../shared/darkSelectStyles';

import Flatpickr from "react-flatpickr";
import DatePicker from "react-datepicker";

import PromoCodeList, { PROMO_CODES_PER_PAGE } from "./PromoCodeList";

const axios = require("axios").default;

const selectStyles = mergeSelectStyles({
  container: base => ({ ...base, zIndex: "999" }),
  control: base => ({ ...base, border: "1px solid var(--od-c-e6e6e6, #e6e6e6)" }),
});

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

const parseDateTime = (dateTime) => {
  if (dateTime){
    if (typeof(dateTime) === "string") {
      var dateObj = new Date(dateTime);
      return dateObj;
    } else {
      return dateTime;
    }
  }
}

const datesAreOnSameDay = (first, second) => {
  return (
    first.getFullYear() === second.getFullYear()
      && first.getMonth() === second.getMonth()
      && first.getDate() === second.getDate()
  );
};

const timeOnDate = (date, time) => {
  date = parseDateTime(date);
  time = parseDateTime(time);

  if(!date || !time){
    return null;
  }

  var month = date.getUTCMonth() + 1;
  var day   = date.getUTCDate();
  var year  = date.getUTCFullYear();

  var copiedTime = new Date(time.getTime());
  copiedTime.setFullYear(year, month, day);

  return copiedTime;
};

const canSubmitPromoCodeForm = (promoCode) => {
  var amount;
  if(promoCode.amount_two_decimals){
    amount = parseFloatFromString(promoCode.amount_two_decimals);
  }

  var percentage;
  if(promoCode.percentage){
    percentage = parseFloatFromString(promoCode.percentage);
  }

  var startDate = parseDateTime(promoCode.start_date_midday);
  var startTime = timeOnDate(startDate, promoCode.start_time_with_wrong_date);
  var endDate   = parseDateTime(promoCode.end_date_midday);
  var endTime   = timeOnDate(endDate, promoCode.end_time_with_wrong_date);

  return (
    promoCode
      && promoCode.name
      && startDate
      && startTime
      && endDate
      && endTime
      && (startDate <= endDate || datesAreOnSameDay(startDate, endDate))
      && startTime <= endTime
      && (
        (typeof(amount) === 'number' && amount >= 0)
          || (typeof(percentage) === 'number' && percentage >= 0 && percentage <= 100)
      )
  );
};

const isoToPickerDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);
const pickerDateToIso = (date) => (date
  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  : null);

const filterPassedTime = (time, startDate, startTime, endDate) => {
  var startMonth = startDate.getUTCMonth() + 1;
  var startDay   = startDate.getUTCDate();
  var startYear  = startDate.getUTCFullYear();

  var endMonth = endDate.getUTCMonth() + 1;
  var endDay   = endDate.getUTCDate();
  var endYear  = endDate.getUTCFullYear();

  var sameDate = (
    startMonth === endMonth
      && startDay === endDay
      && startYear === endYear
  );

  if(!sameDate){
    return true;
  }

  var timeOption = new Date(time);
  var copiedStartTime = new Date(startTime.getTime());

  timeOption.setFullYear(startYear, startMonth, startDay);
  copiedStartTime.setFullYear(startYear, startMonth, startDay);

  return timeOption > copiedStartTime;
};

const ticketsPerOrder = (promoCode) => {
  const typed = promoCode.discounted_tickets_per_order;
  if (typed !== null && typed !== undefined && typed !== "") return parseInt(typed, 10);
  return promoCode.limit_to_one_ticket_per_order ? 1 : null;
};
const ticketsPerOrderParam = (promoCode) => {
  const tickets = ticketsPerOrder(promoCode);
  return tickets === null || tickets === 1 ? null : tickets;
};

const UniversalPromoCodeManager = ({
  csrfToken,
  team,
  currentUser,
  paginatedPromoCodes,
  search,
  ticketTypeTemplates,
}) => {
  const perPage = PROMO_CODES_PER_PAGE;
  const [currentPage, setCurrentPage] = useState(1);
  const [promoCodeForForm, setPromoCodeForForm] = useState({});
  const [promoCodes, setPromoCodes] = useState(paginatedPromoCodes.collection);
  const [totalEntries, setTotalEntries] = useState(paginatedPromoCodes.total_entries);
  const [searchTerm, setSearchTerm] = useState(search || '');
  const [loading, setLoading] = useState(false);
  const latestRequest = useRef(0);

  const getPromoCodes = (page, term = searchTerm) => {
    const requestId = ++latestRequest.current;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    setLoading(true);
    return axios.get("/teams/" + team.id + "/universal_promo_codes?format=json", {
        params: {
          page: page,
          per_page: perPage,
          search: term,
        }
      })
      .then(({ data: { collection, total_entries } }) => {
        if (requestId !== latestRequest.current) return;
        setCurrentPage(page);
        setSearchTerm(term);
        setTotalEntries(total_entries);
        setPromoCodes(collection);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) return;
        toast.error("Could not load promo codes, please try again", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  };

  const fetchNewUniversalPromoCode = (csrfToken, team, promoCodeToCopy) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    var url = "/teams/" + team.id + "/universal_promo_codes/new";
    if(promoCodeToCopy){
      url = (url + "?copy_from_id=" + promoCodeToCopy.id);
    }

    axios.get(url)
      .then(({ data }) => {
        setPromoCodeForForm(data);
      });
  };

  const fetchUniversalPromoCodeForEdit = (csrfToken, team, promoCode) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.get("/teams/" + team.id + "/universal_promo_codes/" + promoCode.id + "/edit")
      .then(({ data }) => {
        setPromoCodeForForm(data);
      });
  };

  const deleteUniversalPromoCode = (csrfToken, team, promoCode) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete("/teams/" + team.id + "/universal_promo_codes/" + promoCode.id)
      .then(() => {
        if (promoCodes.length === 1) {
          getPromoCodes(1);
        } else {
          getPromoCodes(currentPage);
        }
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const createUniversalPromoCode = (csrfToken, team, promoCode) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.post("/teams/" + team.id + "/universal_promo_codes", {
        promo_code: {
          name: promoCode.name,
          amount: (promoCode.amount_two_decimals ? parseFloatFromString(promoCode.amount_two_decimals) : null),
          percentage: (promoCode.percentage ? parseFloatFromString(promoCode.percentage) : null),
          start_date_midday: promoCode.start_date_midday,
          start_time_with_wrong_date: promoCode.start_time_with_wrong_date,
          end_date_midday: promoCode.end_date_midday,
          end_time_with_wrong_date: promoCode.end_time_with_wrong_date,
          eligible_event_start_date: promoCode.eligible_event_start_date || null,
          eligible_event_end_date: promoCode.eligible_event_end_date || null,
          unlimited: (promoCode.limit < 1),
          limit: promoCode.limit || 0,
          limit_per_event: promoCode.limit_per_event || 0,
          limit_to_one_ticket_per_order: ticketsPerOrder(promoCode) === 1,
          discounted_tickets_per_order: ticketsPerOrderParam(promoCode),
          uses_per_fan: parseInt(promoCode.uses_per_fan, 10) || null,
          applies_to_ticket_types: promoCode.applies_to_ticket_types,
          applies_to_add_ons: promoCode.applies_to_add_ons,
          reveal_hidden_tickets: promoCode.reveal_hidden_tickets,
          ticket_type_ids: promoCode.ticket_type_ids,
          applies_to_entire_order: promoCode.applies_to_entire_order,
          discounts_only_selected_ticket_types: promoCode.discounts_only_selected_ticket_types,
          discounted_ticket_type_ids: promoCode.discounted_ticket_type_ids,
        }
      })
      .then(() => {
        const lastPage = searchTerm.trim()
          ? currentPage
          : Math.ceil((totalEntries + 1) / perPage);
        setPromoCodeForForm({});
        getPromoCodes(lastPage);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const updateUniversalPromoCode = (csrfToken, team, promoCode) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.patch("/teams/" + team.id + "/universal_promo_codes/" + promoCode.id, {
        promo_code: {
          name: promoCode.name,
          amount: (promoCode.amount_two_decimals ? parseFloatFromString(promoCode.amount_two_decimals) : null),
          percentage: (promoCode.percentage ? parseFloatFromString(promoCode.percentage) : null),
          start_date_midday: promoCode.start_date_midday,
          start_time_with_wrong_date: promoCode.start_time_with_wrong_date,
          end_date_midday: promoCode.end_date_midday,
          end_time_with_wrong_date: promoCode.end_time_with_wrong_date,
          eligible_event_start_date: promoCode.eligible_event_start_date || null,
          eligible_event_end_date: promoCode.eligible_event_end_date || null,
          unlimited: (promoCode.limit < 1),
          limit: promoCode.limit || 0,
          limit_per_event: promoCode.limit_per_event || 0,
          limit_to_one_ticket_per_order: ticketsPerOrder(promoCode) === 1,
          discounted_tickets_per_order: ticketsPerOrderParam(promoCode),
          uses_per_fan: parseInt(promoCode.uses_per_fan, 10) || null,
          applies_to_ticket_types: promoCode.applies_to_ticket_types,
          applies_to_add_ons: promoCode.applies_to_add_ons,
          reveal_hidden_tickets: promoCode.reveal_hidden_tickets,
          ticket_type_ids: promoCode.ticket_type_ids,
          applies_to_entire_order: promoCode.applies_to_entire_order,
          discounts_only_selected_ticket_types: promoCode.discounts_only_selected_ticket_types,
          discounted_ticket_type_ids: promoCode.discounted_ticket_type_ids,
        }
      })
      .then(() => {
        setPromoCodeForForm({});
        getPromoCodes(currentPage);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  return(
    <div>
      <ToastContainer icon={false} />
      <PromoCodeList
        promoCodes={promoCodes}
        totalEntries={totalEntries}
        currentPage={currentPage}
        loading={loading}
        initialSearch={search}
        hasSearch={searchTerm.trim().length > 0}
        csrfToken={csrfToken}
        team={team}
        onSearch={(term) => getPromoCodes(1, term.trim())}
        onPageChange={(page) => getPromoCodes(page)}
        onAdd={() => fetchNewUniversalPromoCode(csrfToken, team)}
        onEdit={(promoCode) => fetchUniversalPromoCodeForEdit(csrfToken, team, promoCode)}
        onCopy={(promoCode) => fetchNewUniversalPromoCode(csrfToken, team, promoCode)}
        onDelete={(promoCode) => deleteUniversalPromoCode(csrfToken, team, promoCode)}
        onImportClose={() => getPromoCodes(currentPage)}
      />
      <SlidingPane
        isOpen={Object.keys(promoCodeForForm).length > 0}
        className="sliding-pane-container"
        width={"525px"}
        hideHeader={true}
        onRequestClose={() => {
          setPromoCodeForForm({});
        }}
      >
        <React.Fragment>
          <div className="row m-0 py-4"
              style={{"borderBottom": "2px solid var(--od-c-fafafa, #fafafa)"}}>
            <div className="col pl-4">
              <h3 className="mb-0"
                  style={{"fontSize": "20px"}}>
                <strong>Add Promo Code</strong>
              </h3>
            </div>
            <div className="col-xs-auto"
                style={{"paddingRight": "39px"}}>
              <a href="#"
                className="text-muted"
                style={{"fontSize": "22px"}}
                onClick={
                  (e) => {
                    e.preventDefault();
                    setPromoCodeForForm({});
                  }
                }>
                <i className="fal fa-times"></i>
              </a>
            </div>
          </div>
          <form onSubmit={
                  (e) => {
                    e.preventDefault();

                    if(!canSubmitPromoCodeForm(promoCodeForForm)){
                      return false;
                    }

                    if(promoCodeForForm.id){
                      updateUniversalPromoCode(csrfToken, team, promoCodeForForm);
                    } else {
                      createUniversalPromoCode(csrfToken, team, promoCodeForForm);
                    }
                  }
                }>
            <div className="row m-0">
              <div className="col-12 pt-3">
                <div className="form-group">
                  <label className="text-muted mb-1"
                        htmlFor="promo-code-name"
                        style={{"fontSize": "14px"}}>
                    Code Name
                    <span className="text-danger">&nbsp;*</span>
                  </label>
                  <MaskedInput
                    style={{"textTransform": "uppercase"}}
                    mask={promoCodeMask()}
                    className="form-control form-control-slim"
                    guide={false}
                    id="promo-code-name"
                    onBlur={() => {}}
                    value={promoCodeForForm.name || ""}
                    onChange={(e) => {
                      var newName = e.target.value.toUpperCase();
                      var updated = Object.assign({}, promoCodeForForm, {name: newName});

                      setPromoCodeForForm(updated);
                    }}
                  />
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="form-group mb-2">
                      <div className="custom-control custom-checkbox custom-checkbox-table">
                        <input type="checkbox"
                              className="custom-control-input"
                              style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)"}}
                              checked={promoCodeForForm.applies_to_ticket_types || false}
                              onChange={
                                (e) => {
                                  var applyToTicketTypes = e.target.checked;
                                  var params = {
                                    applies_to_ticket_types: applyToTicketTypes
                                  };

                                  if(!applyToTicketTypes){
                                    params = Object.assign({}, params, {
                                      reveal_hidden_tickets: false,
                                      ticket_type_ids: [],
                                      discounts_only_selected_ticket_types: false,
                                      discounted_ticket_type_ids: []
                                    });
                                  }

                                  var updated = Object.assign({}, promoCodeForForm, params);
                                  setPromoCodeForForm(updated);
                                }
                              }
                              id="promo-code-applies-to-ticket-types" />
                        <label className="custom-control-label"
                              style={{"fontSize": "16px"}}
                              htmlFor="promo-code-applies-to-ticket-types">
                          Applies to Tickets
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                {promoCodeForForm.applies_to_ticket_types ? (
                  <div className="form-row" style={{"marginLeft": "12px"}}>
                    <div className="col">
                      <div className="form-group mb-2">
                        <div className="custom-control custom-checkbox custom-checkbox-table">
                          <input
                            type="checkbox"
                            id="promo-code-reveal-hidden-tickets"
                            className="custom-control-input"
                            style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)"}}
                            checked={promoCodeForForm.reveal_hidden_tickets || false}
                            onChange={e => {
                              setPromoCodeForForm({
                                ...promoCodeForForm,
                                reveal_hidden_tickets: e.target.checked,
                                ticket_type_ids: []
                              });
                            }} />
                          <label
                            className="custom-control-label"
                            style={{"fontSize": "16px"}}
                            htmlFor="promo-code-reveal-hidden-tickets"
                          >
                            Reveals hidden templated tickets during checkout
                          </label>
                        </div>
                      </div>
                      {promoCodeForForm.reveal_hidden_tickets ? (
                        <div className="form-group mb-2">
                          <Select
                            styles={selectStyles}
                            placeholder={"Select templated ticket(s)"}
                            isMulti={true}
                            options={
                              ticketTypeTemplates.map(tt => ({ value: tt.id, label: tt.name }))
                            }
                            value={
                              ticketTypeTemplates
                                .map(tt => ({ value: tt.id, label: tt.name }))
                                .filter(tt => promoCodeForForm.ticket_type_ids.includes(tt.value))
                            }
                            onChange={selected => {
                              var ticketTypeIds = (selected || []).map(tt => tt.value);

                              setPromoCodeForForm({
                                ...promoCodeForForm,
                                ticket_type_ids: ticketTypeIds
                              });
                            }} />
                        </div>
                      ) : null}
                      <div className="form-group mb-2">
                        <div className="custom-control custom-checkbox custom-checkbox-table">
                          <input
                            type="checkbox"
                            id="promo-code-discounts-only-selected-ticket-types"
                            className="custom-control-input"
                            style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)"}}
                            checked={promoCodeForForm.discounts_only_selected_ticket_types || false}
                            onChange={e => {
                              setPromoCodeForForm({
                                ...promoCodeForForm,
                                discounts_only_selected_ticket_types: e.target.checked,
                                discounted_ticket_type_ids: []
                              });
                            }} />
                          <label
                            className="custom-control-label"
                            style={{"fontSize": "16px"}}
                            htmlFor="promo-code-discounts-only-selected-ticket-types"
                          >
                            Only applies discount to selected templated tickets
                          </label>
                        </div>
                      </div>
                      {promoCodeForForm.discounts_only_selected_ticket_types ? (
                        <div className="form-group mb-2">
                          <Select
                            styles={selectStyles}
                            placeholder={"Select templated ticket(s)"}
                            isMulti={true}
                            options={
                              ticketTypeTemplates.map(tt => ({ value: tt.id, label: tt.name }))
                            }
                            value={
                              ticketTypeTemplates
                                .map(tt => ({ value: tt.id, label: tt.name }))
                                .filter(tt => (promoCodeForForm.discounted_ticket_type_ids || []).includes(tt.value))
                            }
                            onChange={selected => {
                              setPromoCodeForForm({
                                ...promoCodeForForm,
                                discounted_ticket_type_ids: (selected || []).map(tt => tt.value)
                              });
                            }} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="form-row">
                  <div className="col">
                    <div className="form-group mb-2">
                      <div className="custom-control custom-checkbox custom-checkbox-table">
                        <input type="checkbox"
                              className="custom-control-input"
                              style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)"}}
                              checked={promoCodeForForm.applies_to_add_ons || false}
                              onChange={
                                (e) => {
                                  var updated = Object.assign({}, promoCodeForForm, {
                                    applies_to_add_ons: e.target.checked
                                  });

                                  setPromoCodeForForm(updated);
                                }
                              }
                              id="promo-code-applies-to-add-ons" />
                        <label className="custom-control-label"
                              style={{"fontSize": "16px"}}
                              htmlFor="promo-code-applies-to-add-ons">
                          Applies to Add-ons
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <p style={{"fontSize": "16px"}}>
                      <strong>Quantity Available</strong>
                    </p>
                    <div className="form-group">
                      <label className="text-muted mb-1"
                        htmlFor="promo-code-available"
                        style={{"fontSize": "14px"}}>
                        Total number available
                      </label>
                      <MaskedInput
                        id="promo-code-available"
                        className="form-control form-control-slim"
                        value={promoCodeForForm.limit || ""}
                        mask={createNumberMask({
                          prefix: "",
                          decimalLimit: 0
                        })}
                        onChange={e => {
                          var updated = {...promoCodeForForm}
                          var blank = (e.target.value === "")

                          updated.limit = blank ? 0 : e.target.value;
                          updated.unlimited = blank;

                          setPromoCodeForForm(updated);
                        }} />
                      <span className="text-muted small">Leave field blank for unlimited.</span>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="form-group">
                      <label className="text-muted mb-1"
                        htmlFor="promo-code-available"
                        style={{"fontSize": "14px"}}>
                        Number available per event
                      </label>
                      <MaskedInput
                        id="promo-code-available-per-event"
                        className="form-control form-control-slim"
                        value={promoCodeForForm.limit_per_event || ""}
                        mask={createNumberMask({
                          prefix: "",
                          decimalLimit: 0
                        })}
                        onChange={e => {
                          setPromoCodeForForm({
                            ...promoCodeForForm,
                            limit_per_event: e.target.value === "" ? 0 : e.target.value
                          });
                        }} />
                      <span className="text-muted small">Leave field blank for unlimited.</span>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="form-group">
                      <TextInput
                        as={MaskedInput}
                        id="promo-code-discounted-tickets-per-order"
                        label="Discounted tickets per order"
                        trailingText="tickets"
                        value={ticketsPerOrder(promoCodeForForm) ?? ""}
                        mask={createNumberMask({
                          prefix: "",
                          includeThousandsSeparator: false,
                          decimalLimit: 0
                        })}
                        onChange={e => {
                          setPromoCodeForForm({
                            ...promoCodeForForm,
                            limit_to_one_ticket_per_order: false,
                            discounted_tickets_per_order: e.target.value === "" ? null : e.target.value
                          });
                        }} />
                      <span className="text-muted small">Leave field blank to discount every ticket. More tickets in the same order are full price.</span>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="form-group">
                      <TextInput
                        as={MaskedInput}
                        id="promo-code-uses-per-fan"
                        label="Uses per fan"
                        trailingText="orders"
                        value={promoCodeForForm.uses_per_fan ?? ""}
                        mask={createNumberMask({
                          prefix: "",
                          includeThousandsSeparator: false,
                          decimalLimit: 0
                        })}
                        onChange={e => {
                          setPromoCodeForForm({
                            ...promoCodeForForm,
                            uses_per_fan: e.target.value === "" ? null : e.target.value
                          });
                        }}
                        hint="How many orders one fan (one email) can use this code on. Leave blank for no limit." />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <p style={{"fontSize": "16px"}}>
                      <strong>Discount</strong>
                    </p>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="form-group">
                      <label className="text-muted mb-1"
                            htmlFor="promo-code-amount"
                            style={{"fontSize": "14px"}}>
                        Discount Amount
                      </label>
                      <CurrencyInput className="form-control form-control-slim"
                                    maskOptions={{
                                      prefix: "",
                                      integerLimit: null
                                    }}
                                    placeholder="$"
                                    value={promoCodeForForm.amount_two_decimals || ""}
                                    onChange={
                                      (e) => {
                                        var updated = Object.assign({}, promoCodeForForm, {
                                          amount_two_decimals: e.target.value,
                                          percentage: null
                                        });

                                        setPromoCodeForForm(updated);
                                      }
                                    }
                                    id="promo-code-amount" />
                    </div>
                  </div>
                  <div className="col-xs-auto d-flex align-items-center justify-content-center">
                    <div style={{"fontSize": "16px"}}>
                      OR
                    </div>
                  </div>
                  <div className="col">
                    <div className="form-group">
                      <label className="text-muted mb-1"
                            htmlFor="promo-code-percentage"
                            style={{"fontSize": "14px"}}>
                        &nbsp;
                      </label>
                      <MaskedInput className={"form-control form-control-slim " + (parseFloatFromString(promoCodeForForm.percentage || "") > 100 ? "highlight" : "")}
                                  mask={createNumberMask({
                                    prefix: "",
                                    decimalLimit: 0
                                  })}
                                  id="promo-code-percentage"
                                  placeholder="%"
                                  onChange={
                                    (e) => {
                                      var updated = Object.assign({}, promoCodeForForm, {
                                        percentage: e.target.value,
                                        amount_two_decimals: null,
                                        applies_to_entire_order: false
                                      });

                                      setPromoCodeForForm(updated);
                                    }
                                  }
                                  value={typeof(promoCodeForForm.percentage) !== "undefined" ? promoCodeForForm.percentage : ""} />
                    </div>
                  </div>
                </div>
                {promoCodeForForm.amount_two_decimals ? (
                  <div className="form-row">
                    <div className="col">
                      <div className="form-group mb-2">
                        <div className="custom-control custom-checkbox custom-checkbox-table">
                          <input
                            id="promo-code-applies-to-entire-order"
                            type="checkbox"
                            className="custom-control-input"
                            style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)"}}
                            checked={promoCodeForForm.applies_to_entire_order || false}
                            onChange={e => {
                              setPromoCodeForForm({
                                ...promoCodeForForm,
                                applies_to_entire_order: e.target.checked,
                                limit_to_one_ticket_per_order: (e.target.checked ? false : promoCodeForForm.limit_to_one_ticket_per_order),
                                discounted_tickets_per_order: (e.target.checked ? null : promoCodeForForm.discounted_tickets_per_order),
                                limit_to_one_add_on_per_order: (e.target.checked ? false : promoCodeForForm.limit_to_one_add_on_per_order)
                              });
                            }}
                          />
                          <label
                            className="custom-control-label"
                            style={{"fontSize": "16px"}}
                            htmlFor="promo-code-applies-to-entire-order"
                          >
                            Apply amount to entire order
                          </label>
                        </div>
                        <small className="form-text text-muted">
                          {promoCodeForForm.applies_to_entire_order ? (
                            "The discount amount is taken once off the whole order and split across its eligible items. Fees are still charged on each discounted item."
                          ) : (
                            "The discount amount is taken off each eligible item."
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="form-row">
                  <div className="col-12">
                    <p style={{"fontSize": "16px"}}>
                      <strong>Promo Code Starts</strong>
                    </p>
                    <div className="form-row">
                      <div className="col">
                        <div className="form-group">
                          <label htmlFor="promo-code-start-date"
                                className="text-muted mb-1"
                                style={{"fontSize": "14px"}}>
                            Start Date
                            <span className="text-danger">&nbsp;*</span>
                          </label>
                          <Flatpickr
                            options={{
                              dateFormat: "M j, Y"
                            }}
                            placeholder={"Select date"}
                            className={"form-control ticketing-date-picker form-control-flatpickr"}
                            value={parseDateTime(promoCodeForForm.start_date_midday)}
                            onChange={dates => {
                              var startDate = parseDateTime(dates[0]);
                              var startTime = parseDateTime(promoCodeForForm.start_time_with_wrong_date);
                              var endDate   = parseDateTime(promoCodeForForm.end_date_midday);
                              var endTime   = parseDateTime(promoCodeForForm.end_time_with_wrong_date);

                              var attrs = {
                                start_date_midday: startDate
                              };

                              if(endDate && endDate < startDate){
                                endDate = "";
                                attrs = Object.assign({}, attrs, {
                                  end_date_midday: endDate
                                });
                              }

                              if(startDate && startTime && endDate && endTime){
                                var startTimeCorrectDate = timeOnDate(startDate, startTime);
                                var endTimeCorrectDate = timeOnDate(endDate, endTime);

                                if(endTimeCorrectDate < startTimeCorrectDate){
                                  attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                                }
                              }

                              var updated = Object.assign({}, promoCodeForForm, attrs);
                              setPromoCodeForForm(updated);
                            }}
                          />
                        </div>
                      </div>
                      <div className="col">
                        <div className="form-group">
                          <label htmlFor="promo-code-start-time"
                                className="text-muted mb-1"
                                style={{"fontSize": "14px"}}>
                            Start Time
                            <span className="text-danger">&nbsp;*</span>
                          </label>
                          <DatePicker
                            className="form-control ticketing-date-picker"
                            selected={parseDateTime(promoCodeForForm.start_time_with_wrong_date)}
                            placeholderText={"Select time"}
                            onChange={(time) => {
                              var startDate = parseDateTime(promoCodeForForm.start_date_midday);
                              var startTime = parseDateTime(time);
                              var endDate   = parseDateTime(promoCodeForForm.end_date_midday);
                              var endTime   = parseDateTime(promoCodeForForm.end_time_with_wrong_date);

                              var attrs = {
                                start_time_with_wrong_date: startTime
                              };

                              if(startDate && endDate && endTime){
                                var startTimeCorrectDate = timeOnDate(startDate, startTime);
                                var endTimeCorrectDate = timeOnDate(endDate, endTime);

                                if(endTimeCorrectDate < startTimeCorrectDate){
                                  attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                                }
                              }

                              var updated = Object.assign({}, promoCodeForForm, attrs);
                              setPromoCodeForForm(updated);
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            dateFormat="h:mm aa"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col-12">
                    <p style={{"fontSize": "16px"}}>
                      <strong>Promo Code Ends</strong>
                    </p>
                    <div className="form-row">
                      <div className="col">
                        <div className="form-group">
                          <label htmlFor="promo-code-end-date"
                                className="text-muted mb-1"
                                style={{"fontSize": "14px"}}>
                            Expiration Date
                            <span className="text-danger">&nbsp;*</span>
                          </label>
                          <Flatpickr
                            options={{
                              dateFormat: "M j, Y",
                              minDate: (promoCodeForForm.start_date_midday ? (
                                parseDateTime(promoCodeForForm.start_date_midday).setHours(0,0,0,0)
                              ) : null)
                            }}
                            disabled={!promoCodeForForm.start_date_midday}
                            placeholder={"Select date"}
                            className={"form-control ticketing-date-picker form-control-flatpickr " + (!promoCodeForForm.start_date_midday ? "form-control-disabled" : "")}
                            value={parseDateTime(promoCodeForForm.end_date_midday)}
                            onChange={dates => {
                              var startDate = parseDateTime(promoCodeForForm.start_date_midday);
                              var startTime = parseDateTime(promoCodeForForm.start_time_with_wrong_date);
                              var endDate   = parseDateTime(dates[0]);
                              var endTime   = parseDateTime(promoCodeForForm.end_time_with_wrong_date);

                              var attrs = {
                                end_date_midday: endDate
                              };

                              if(startDate && startTime && endTime){
                                var startTimeCorrectDate = timeOnDate(startDate, startTime);
                                var endTimeCorrectDate = timeOnDate(endDate, endTime);

                                if(endTimeCorrectDate < startTimeCorrectDate){
                                  attrs = Object.assign({}, attrs, {end_time_with_wrong_date: ""});
                                }
                              }

                              var updated = Object.assign({}, promoCodeForForm, attrs);
                              setPromoCodeForForm(updated);
                            }}
                          />
                        </div>
                      </div>
                      <div className="col">
                        <div className="form-group">
                          <label htmlFor="promo-code-end-time"
                                className="text-muted mb-1"
                                style={{"fontSize": "14px"}}>
                            Expiration Time
                            <span className="text-danger">&nbsp;*</span>
                          </label>
                          <DatePicker
                            className={"form-control ticketing-date-picker " + (!promoCodeForForm.end_date_midday || !promoCodeForForm.start_time_with_wrong_date ? "form-control-disabled" : "")}
                            selected={parseDateTime(promoCodeForForm.end_time_with_wrong_date)}
                            placeholderText={"Select time"}
                            disabled={!promoCodeForForm.end_date_midday || !promoCodeForForm.start_time_with_wrong_date}
                            onChange={(time) => {
                              var updated = Object.assign({}, promoCodeForForm, {
                                end_time_with_wrong_date: time
                              });

                              setPromoCodeForForm(updated);
                            }}
                            filterTime={
                              (time) => {
                                return filterPassedTime(
                                  time,
                                  parseDateTime(promoCodeForForm.start_date_midday),
                                  parseDateTime(promoCodeForForm.start_time_with_wrong_date),
                                  parseDateTime(promoCodeForForm.end_date_midday)
                                );
                              }
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            dateFormat="h:mm aa"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col-12">
                    <SetlistDatePicker
                      mode="range"
                      label="Only for shows between"
                      hint="Optional. Leave blank to allow every show."
                      placeholder="Any show date"
                      value={promoCodeForForm.eligible_event_start_date ? {start: isoToPickerDate(promoCodeForForm.eligible_event_start_date), end: isoToPickerDate(promoCodeForForm.eligible_event_end_date)} : null}
                      onChange={(range) => {
                        var updated = Object.assign({}, promoCodeForForm, {
                          eligible_event_start_date: pickerDateToIso(range && range.start),
                          eligible_event_end_date: pickerDateToIso(range && range.end)
                        });

                        setPromoCodeForForm(updated);
                      }}
                    />
                    {(promoCodeForForm.eligible_event_start_date || promoCodeForForm.eligible_event_end_date) ? (
                      <Button
                        id="clear-show-dates"
                        variant="link-color"
                        type="button"
                        size="sm"
                        onClick={() => {
                          setPromoCodeForForm(Object.assign({}, promoCodeForForm, {
                            eligible_event_start_date: null,
                            eligible_event_end_date: null
                          }));
                        }}
                      >
                        Allow every show
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="row mt-1">
                  <div className="col-12 text-right my-4">
                    <a href="#"
                      onClick={
                        (e) => {
                          e.preventDefault();
                          setPromoCodeForForm({});
                        }
                      }
                      className="btn btn-outline-primary">
                      <strong>Cancel</strong>
                    </a>
                    <button type="submit"
                            disabled={!canSubmitPromoCodeForm(promoCodeForForm)}
                            className="btn btn-primary ml-3">
                      <strong>Save</strong>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </React.Fragment>
      </SlidingPane>
    </div>
  );
};

UniversalPromoCodeManager.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  paginatedPromoCodes: PropTypes.object.isRequired,
  search: PropTypes.string,
  ticketTypeTemplates: PropTypes.array.isRequired,
};

export default UniversalPromoCodeManager;
